import mongoose from "mongoose"
import { accessibleRecordsPlugin } from "@casl/mongoose"

import * as frameworkSchemas from "./models"
import { rtsChangeLogPlugin } from "./rtsChangeLogPlugin"
import { registerPoliciesFromModules } from "./acl"
import { mongooseAclPlugin } from "./acl/mongooseAclPlugin"
import { mongoPaginationPlugin } from "./pagination/mongoPaginationPlugin"


type ModelModules = Record<string, unknown>
type ModelsScope = "tenant" | "global"
type RegisterModelsOptions = {
  allowReservedRbModelNames?: boolean;
}

type RegisteredSchemas = Record<string, mongoose.Schema>

type RegisteredModels = {
  tenant: RegisteredSchemas
  global: RegisteredSchemas
}

let cachedModels: RegisteredModels | null = null

const DEFAULT_GLOBAL_RB_MODEL_NAMES_SET = new Set<string>([
  "RBUser",
  "RBTenant",
  "RBOAuthRequest",
])

const assertSchema = (exportName: string, value: unknown): mongoose.Schema => {
  if (value instanceof mongoose.Schema) return value

  throw new Error(
    [
      `Expected ${exportName} to be an instance of mongoose.Schema, but it was not.`,
      "rpcbase supports mongoose 9+ only.",
      "Fix: ensure the project is using mongoose 9.x and that all packages resolve the same mongoose instance (try `npm ls mongoose`).",
    ].join(" "),
  )
}

const getFrameworkSchemaForModelName = (modelName: string): mongoose.Schema | null => {
  const exportName = `${modelName}Schema`
  const value = (frameworkSchemas as unknown as Record<string, unknown>)[exportName]
  if (!(value instanceof mongoose.Schema)) return null
  return value
}

const applyTenantPlugins = (schema: mongoose.Schema): void => {
  schema.plugin(accessibleRecordsPlugin)
  schema.plugin(mongooseAclPlugin)
  schema.plugin(mongoPaginationPlugin)
  schema.plugin(rtsChangeLogPlugin)
}

const registerSchema = (
  target: RegisteredSchemas,
  other: RegisteredSchemas,
  modelName: string,
  schema: mongoose.Schema,
  scope: ModelsScope,
): void => {
  if (target[modelName] || other[modelName]) {
    throw new Error(`Duplicate model name "${modelName}" across tenant/global scopes`)
  }
  target[modelName] = schema
}

const buildSchemasFromModules = (modules: ModelModules): Array<{ modelName: string; schema: mongoose.Schema }> =>
  Object.entries(modules)
    .filter(([key]) => key.endsWith("Schema"))
    .map(([key, schemaValue]) => {
      const schema = assertSchema(key, schemaValue)
      const modelName = key.replace(/Schema$/, "")
      return { modelName, schema }
    })

export const registerModels = (
  { tenant, global }: { tenant: ModelModules; global?: ModelModules },
  options: RegisterModelsOptions = {},
) => {
  registerPoliciesFromModules(frameworkSchemas as unknown as Record<string, unknown>)
  registerPoliciesFromModules(tenant)

  const tenantSchemas: RegisteredSchemas = {}
  const globalSchemas: RegisteredSchemas = {}
  const allowReservedRbModelNames = options.allowReservedRbModelNames === true

  for (const { modelName, schema } of buildSchemasFromModules(frameworkSchemas as unknown as ModelModules)) {
    if (DEFAULT_GLOBAL_RB_MODEL_NAMES_SET.has(modelName)) {
      const cloned = schema.clone()
      registerSchema(globalSchemas, tenantSchemas, modelName, cloned, "global")
    } else {
      const cloned = schema.clone()
      applyTenantPlugins(cloned)
      registerSchema(tenantSchemas, globalSchemas, modelName, cloned, "tenant")
    }
  }

  for (const { modelName, schema } of buildSchemasFromModules(tenant)) {
    if (modelName === "RBUser" || modelName === "RBTenant") {
      throw new Error(`Invalid tenant model name "${modelName}". RBUser/RBTenant are global models.`)
    }
    if (modelName.startsWith("RB")) {
      const frameworkSchema = getFrameworkSchemaForModelName(modelName)
      if (frameworkSchema && schema === frameworkSchema) continue
      if (!allowReservedRbModelNames) {
        throw new Error(`Invalid tenant model name "${modelName}". RB* models are reserved for rpcbase.`)
      }
    }
    const cloned = schema.clone()
    applyTenantPlugins(cloned)
    registerSchema(tenantSchemas, globalSchemas, modelName, cloned, "tenant")
  }

  for (const { modelName, schema } of buildSchemasFromModules(global ?? {})) {
    if (modelName.startsWith("RB")) {
      const frameworkSchema = getFrameworkSchemaForModelName(modelName)
      if (frameworkSchema && schema === frameworkSchema) continue
      if (!allowReservedRbModelNames) {
        throw new Error(`Invalid global model name "${modelName}". RB* models are reserved for rpcbase.`)
      }
    }
    const cloned = schema.clone()
    registerSchema(globalSchemas, tenantSchemas, modelName, cloned, "global")
  }

  const allSchemas = { ...globalSchemas, ...tenantSchemas }

  for (const [modelName, schema] of Object.entries(allSchemas)) {
    if (!mongoose.models[modelName]) {
      mongoose.model(modelName, schema)
    }
  }

  cachedModels = {
    tenant: { ...(cachedModels?.tenant ?? {}), ...tenantSchemas },
    global: { ...(cachedModels?.global ?? {}), ...globalSchemas },
  }
}

export const getRegisteredModels = (scope: ModelsScope) => {
  if (!cachedModels) {
    throw new Error("Models not registered. Call createModels(...) once at startup (or import your models module) before using models.get.")
  }
  return cachedModels[scope]
}

export type { ModelModules, ModelsScope, RegisterModelsOptions }
